import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { portfolioData } from '../data/mock';
import { ChevronDown, ChevronUp, Briefcase, MapPin } from 'lucide-react';
import { Card } from './ui/card';

const ExperienceSection = () => {
  const { experience } = portfolioData;
  const [expandedId, setExpandedId] = useState(1);
  const [expandAll, setExpandAll] = useState(false);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Expand every card right before the browser prints / saves to PDF,
  // then revert when the print dialog closes.
  useEffect(() => {
    const handleBeforePrint = () => {
      flushSync(() => setExpandAll(true));
    };
    const handleAfterPrint = () => setExpandAll(false);
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  return (
    <section id="experience" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f1419]">
      <div className="max-w-5xl mx-auto">
        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            <span className="text-emerald-400 font-mono text-xl">02.</span> Work Experience
          </h2>
          <div className="h-px bg-gray-700 mt-4"></div>
        </div>

        <div className="space-y-4">
          {experience.map((job) => {
            const isOpen = expandAll || expandedId === job.id;
            return (
              <Card
                key={job.id}
                className="bg-[#1a1d29] border-gray-800 hover:border-emerald-500/50 transition-all duration-300 overflow-hidden"
                data-testid={`experience-card-${job.id}`}
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => toggleExpand(job.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                          <Briefcase size={20} className="text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{job.role}</h3>
                          <p className="text-emerald-400 font-semibold">{job.company}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 ml-13">
                        <div className="flex items-center gap-1">
                          <MapPin size={14} />
                          <span>{job.location}</span>
                        </div>
                        <span>•</span>
                        <span>{job.period}</span>
                        {job.current && (
                          <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium">
                            Current
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="experience-toggle-icon text-gray-400 hover:text-emerald-400 transition-colors flex-shrink-0"
                      aria-label={isOpen ? 'Collapse' : 'Expand'}
                    >
                      {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="mt-6 ml-13 space-y-3">
                      {job.achievements.map((achievement, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-gray-300">
                          <span className="text-emerald-400 mt-1 flex-shrink-0">▹</span>
                          <p className="leading-relaxed">{achievement}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
